import { useTranslation } from 'react-i18next'

import { Button } from '#/shared/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '#/shared/ui/dialog'
import { CloseIcon } from '#/shared/ui/icons'
import { Skeleton } from '#/shared/ui/skeleton'

import { groupEntriesByDay } from '../model/group-entries-by-day'
import { ACTIVITY_ENTRY_KIND } from '../model/types'
import type { ActivityHistoryEntry } from '../model/types'
import { ActivityHistoryEventRow } from './activity-history-event-row'
import { ActivityHistorySessionCard } from './activity-history-session-card'

interface ActivityHistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entries: ActivityHistoryEntry[]
  totalCount?: number
  isLoading?: boolean
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  onExportCsv?: () => void
}

function DialogHeaderBar({ title }: { title: string }) {
  const { t } = useTranslation()
  return (
    <header className="flex shrink-0 items-center justify-between border border-gray-200 bg-white px-5 py-2.5">
      <DialogTitle className="text-base font-semibold text-gray-500">
        {title}
      </DialogTitle>
      <DialogClose
        aria-label={t('activityHistory.close')}
        className="flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900"
      >
        <CloseIcon className="size-3.5" />
      </DialogClose>
    </header>
  )
}

function DialogFooterBar({
  shownCount,
  totalCount,
  onExportCsv,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: {
  shownCount: number
  totalCount: number | undefined
  onExportCsv?: () => void
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
}) {
  const { t } = useTranslation()
  return (
    <footer className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-white px-5 py-3">
      <p className="text-sm text-gray-500">
        {totalCount === undefined
          ? t('activityHistory.footer.shownOnly', { shown: shownCount })
          : t('activityHistory.footer.shownOf', {
              shown: shownCount,
              total: totalCount,
            })}
      </p>
      <div className="flex items-center gap-2">
        {onExportCsv && (
          <Button
            variant="outline"
            size="xs"
            pressEffect
            className="px-3.5"
            onClick={onExportCsv}
          >
            {t('activityHistory.actions.exportCsv')}
          </Button>
        )}
        {hasMore && onLoadMore && (
          <Button
            variant="outline"
            size="xs"
            pressEffect
            className="px-3.5"
            disabled={isLoadingMore}
            onClick={onLoadMore}
          >
            {isLoadingMore
              ? t('table.saving')
              : t('activityHistory.actions.loadMore')}
          </Button>
        )}
      </div>
    </footer>
  )
}

function EntryRenderer({ entry }: { entry: ActivityHistoryEntry }) {
  if (entry.kind === ACTIVITY_ENTRY_KIND.event) {
    return <ActivityHistoryEventRow entry={entry} />
  }
  return <ActivityHistorySessionCard entry={entry} />
}

function SessionSkeleton() {
  return (
    <div className="flex w-full flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3.5">
      <div className="flex items-center gap-3">
        <Skeleton className="size-7 shrink-0 rounded-full" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="flex flex-col gap-2 pl-10">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-2.5">
      <Skeleton className="h-3 w-24" />
      <div className="flex flex-col gap-5">
        <SessionSkeleton />
        <SessionSkeleton />
        <SessionSkeleton />
      </div>
    </div>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div className="flex w-full flex-col items-center gap-2 px-5 py-12 text-center">
      <p className="text-sm font-medium text-gray-900">
        {t('activityHistory.empty.title')}
      </p>
      <p className="text-xs text-gray-500">{t('activityHistory.empty.hint')}</p>
    </div>
  )
}

export function ActivityHistoryDialog({
  open,
  onOpenChange,
  entries,
  totalCount,
  isLoading,
  onLoadMore,
  hasMore,
  isLoadingMore,
  onExportCsv,
}: ActivityHistoryDialogProps) {
  const { t } = useTranslation()
  const groups = groupEntriesByDay(entries, {
    today: t('activityHistory.today'),
    yesterday: t('activityHistory.yesterday'),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-240">
        <DialogHeaderBar title={t('activityHistory.title')} />
        <div className="flex min-h-0 flex-1 flex-col bg-warm-25">
          <div className="flex min-h-0 flex-1 flex-col gap-7.5 overflow-y-auto px-5 py-4">
            {isLoading ? (
              <LoadingState />
            ) : entries.length === 0 ? (
              <EmptyState />
            ) : (
              groups.map((group) => (
                <section key={group.key} className="flex flex-col gap-2.5">
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    {group.label}
                  </p>
                  <div className="flex flex-col gap-5">
                    {group.entries.map((entry) => (
                      <EntryRenderer key={entry.id} entry={entry} />
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>
          {!isLoading && (
            <DialogFooterBar
              shownCount={entries.length}
              totalCount={totalCount}
              onExportCsv={entries.length > 0 ? onExportCsv : undefined}
              onLoadMore={onLoadMore}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
